import { CSSObject } from '@emotion/react'
import * as CSS from 'csstype'

import { theme } from '../constants/theme'

/**
 * Adapted from https://tailwindcss.com/docs/border-radius
 */
type BorderShorthandProps = {
  rounded?: boolean | 'b' | 'bl' | 'br' | 't' | 'tl' | 'tr'
}

/**
 * These `csstype` properties are not compatible with the React attributes of the same name,
 * thus causing type errors when passed to e.g. `styled.div`.
 *
 * By excluding them from our style props, we'll only get the `styled.div` typing.
 */
type CSSTypeReactIncompatibleProperties = 'color' | 'translate'

/**
 * Adapted from https://tailwindcss.com/docs/flex-direction
 */
type FlexProps = {
  flex?:
    | 'col'
    | 'col-reverse'
    | 'row'
    | 'row-reverse'
    // Support shorthands like "2 2 10%" while retaining autocompletion for values like "col":
    | Exclude<CSS.Property.Flex<number | string>, string>
    | `${number}${string}`
}

type GapProps = {
  columnGap?: SpacingValue
  gap?: SpacingValue
  rowGap?: SpacingValue
}

/**
 * Adapted from:
 *  - https://tailwindcss.com/docs/height
 *  - https://tailwindcss.com/docs/margin
 *  - https://tailwindcss.com/docs/padding
 *  - https://tailwindcss.com/docs/width
 */
type SpacingShorthandProps = {
  h?: SpacingValue | 'full'
  m?: SpacingValue
  maxH?: SpacingValue | 'full'
  maxW?: SpacingValue | 'full'
  mb?: SpacingValue
  ml?: SpacingValue
  mr?: SpacingValue
  mt?: SpacingValue
  mx?: SpacingValue
  my?: SpacingValue
  p?: SpacingValue
  pb?: SpacingValue
  pl?: SpacingValue
  pr?: SpacingValue
  pt?: SpacingValue
  px?: SpacingValue
  py?: SpacingValue
  w?: SpacingValue | 'full'
}

const parseBorderShorthandProps = (
  props: BorderShorthandProps,
): CSSProperties => {
  return {
    ...(props.rounded === true && {
      borderRadius: theme.border.radius.normal,
    }),
    ...((props.rounded === 'b' || props.rounded === 'bl') && {
      borderBottomLeftRadius: theme.border.radius.normal,
    }),
    ...((props.rounded === 'b' || props.rounded === 'br') && {
      borderBottomRightRadius: theme.border.radius.normal,
    }),
    ...((props.rounded === 't' || props.rounded === 'tl') && {
      borderTopLeftRadius: theme.border.radius.normal,
    }),
    ...((props.rounded === 't' || props.rounded === 'tr') && {
      borderTopRightRadius: theme.border.radius.normal,
    }),
  }
}

const parseFlexProps = (props: FlexProps): CSSProperties => {
  switch (props.flex) {
    case 'col':
      return { display: 'flex', flexDirection: 'column' }
    case 'col-reverse':
      return { display: 'flex', flexDirection: 'column-reverse' }
    case 'row':
      return { display: 'flex', flexDirection: 'row' }
    case 'row-reverse':
      return { display: 'flex', flexDirection: 'row-reverse' }
    default:
      return props.flex ? { flex: props.flex } : {}
  }
}

const parseGapProps = (props: GapProps): CSSProperties => {
  return {
    ...(props.columnGap !== undefined && {
      columnGap: parseSpacingValue(props.columnGap),
    }),
    ...(props.gap !== undefined && {
      gap: parseSpacingValue(props.gap),
    }),
    ...(props.rowGap !== undefined && {
      rowGap: parseSpacingValue(props.rowGap),
    }),
  }
}

const parseSpacingShorthandProps = (
  props: SpacingShorthandProps,
): CSSProperties => {
  const height = parseSpacingValue(props.h)
  const margin = parseSpacingValue(props.m)
  const marginBottom = parseSpacingValue(props.mb ?? props.my)
  const marginLeft = parseSpacingValue(props.ml ?? props.mx)
  const marginRight = parseSpacingValue(props.mr ?? props.mx)
  const marginTop = parseSpacingValue(props.mt ?? props.my)
  const maxHeight = parseSpacingValue(props.maxH)
  const maxWidth = parseSpacingValue(props.maxW)
  const padding = parseSpacingValue(props.p)
  const paddingBottom = parseSpacingValue(props.pb ?? props.py)
  const paddingLeft = parseSpacingValue(props.pl ?? props.px)
  const paddingRight = parseSpacingValue(props.pr ?? props.px)
  const paddingTop = parseSpacingValue(props.pt ?? props.py)
  const width = parseSpacingValue(props.w)

  return {
    ...(height !== undefined && {
      height: height === 'full' ? '100%' : height,
    }),
    ...(margin !== undefined && { margin }),
    ...(marginBottom !== undefined && { marginBottom }),
    ...(marginLeft !== undefined && { marginLeft }),
    ...(marginRight !== undefined && { marginRight }),
    ...(marginTop !== undefined && { marginTop }),
    ...(maxHeight !== undefined && {
      maxHeight: maxHeight === 'full' ? '100%' : maxHeight,
    }),
    ...(maxWidth !== undefined && {
      maxWidth: maxWidth === 'full' ? '100%' : maxWidth,
    }),
    ...(padding !== undefined && { padding }),
    ...(paddingBottom !== undefined && { paddingBottom }),
    ...(paddingLeft !== undefined && { paddingLeft }),
    ...(paddingRight !== undefined && { paddingRight }),
    ...(paddingTop !== undefined && { paddingTop }),
    ...(width !== undefined && { width: width === 'full' ? '100%' : width }),
  }
}

const parseSpacingValue = (value: SpacingValue | undefined) => {
  if (value === undefined) {
    return undefined
  } else if (typeof value === 'string') {
    return value
  }
  return `${theme.spacing[value]}px`
}

export type CSSProperties = CSS.PropertiesFallback<number | string>

export type SpacingValue =
  | 0
  | 0.5
  | 1
  | 1.5
  | 2
  | 2.5
  | 3
  | 3.5
  | 4
  | 4.5
  | 5
  | 5.5
  | 6
  | 6.5
  | 7
  | 7.5
  | 8
  | string

export type StyleProps = Omit<
  CSSProperties,
  'flex' | 'gap' | CSSTypeReactIncompatibleProperties
> &
  BorderShorthandProps &
  FlexProps &
  GapProps &
  SpacingShorthandProps & {
    color?: string
    translate?: string
  }

const createBaseStyles = (props: StyleProps = {}): CSSObject => {
  const {
    columnGap,
    flex,
    gap,
    h,
    m,
    maxH,
    maxW,
    mb,
    ml,
    mr,
    mt,
    mx,
    my,
    p,
    pb,
    pl,
    pr,
    pt,
    px,
    py,
    rounded,
    rowGap,
    w,
    ...rest
  } = props

  const styles = {
    ...parseBorderShorthandProps({ rounded }),
    ...parseFlexProps({ flex }),
    ...parseGapProps({ columnGap, gap, rowGap }),
    ...parseSpacingShorthandProps({
      h,
      m,
      maxH,
      maxW,
      mb,
      ml,
      mr,
      mt,
      mx,
      my,
      p,
      pb,
      pl,
      pr,
      pt,
      px,
      py,
      w,
    }),
    ...rest,
  }

  return styles
}

/**
 * Utility function for creating CSS styles.
 * Adds support for some Tailwind-style shorthand props.
 */
export const createStyles = (props: StyleProps = {}): CSSObject => {
  return createBaseStyles(props)
}
